import type { SheetSize } from '../components/menu/MenuSheet'

/** The id of the stylesheet this module owns, so it is replaced not stacked. */
const STYLE_ID = 'menu-page-size'

/**
 * Prints the menu card instead of a till slip.
 *
 * Two problems have to be solved together.
 *
 * The first is that the receipt stylesheet claims `@page { size: 80mm auto }`
 * for the whole document, and a page size cannot be scoped to an element, so
 * an A4 menu printed as-is comes out sized for a till roll. The two modes are
 * kept apart by a class on <html>: the menu's layout rules are written under
 * `.printing-menu` and apply only while it is set.
 *
 * The second is that `@page` cannot usefully be named. Named page rules and
 * the `page` property they pair with are in the spec but not in Chrome or
 * Safari, so `@page menu-a4 { size: A4 }` is quietly ignored and the print
 * dialog opens on whatever paper it used last. The only rule browsers honour
 * is the plain, unnamed one, so the right `@page` is written into the
 * document just before printing and removed straight after, which is what
 * makes the dialog open already set to A4 or A5.
 *
 * The stylesheet is removed after `print()` returns. That is safe because
 * `window.print()` blocks until the dialog is dismissed in every browser this
 * runs on; `afterprint` is listened for as well, since Safari on iOS has been
 * known to return early.
 */
export function printMenu(size: SheetSize): void {
  const root = document.documentElement
  const classes = ['printing-menu', `printing-menu-${size}`]

  /*
   * The preview's position is written as an inline style, which no stylesheet
   * can override, left in place it would offset the card by however far down
   * the pane it happened to be sitting, pushing most of it off the sheet. So
   * the two properties are cleared for the print and put back afterwards.
   */
  const sheet = document.getElementById('menu-sheet-root')
  const spot = sheet ? { top: sheet.style.top, left: sheet.style.left } : null
  if (sheet) {
    sheet.style.top = ''
    sheet.style.left = ''
  }

  // Replaced rather than appended, so repeated prints cannot leave two
  // conflicting page rules in the document.
  document.getElementById(STYLE_ID)?.remove()

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `@page { size: ${size === 'a4' ? 'A4' : 'A5'} portrait; margin: 0; }`
  document.head.appendChild(style)

  const clear = () => {
    root.classList.remove(...classes)
    document.getElementById(STYLE_ID)?.remove()
    if (sheet && spot) {
      sheet.style.top = spot.top
      sheet.style.left = spot.left
    }
    window.removeEventListener('afterprint', clear)
  }

  root.classList.add(...classes)
  window.addEventListener('afterprint', clear)

  // Two frames, for the same reason the receipt needs them: the first queues
  // after React commits, the second after the browser has painted with the new
  // class applied. Printing in the same tick would use the old page rules.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        window.print()
      } finally {
        clear()
      }
    })
  })
}

/** The id of the receipt's own page rule, replaced rather than stacked. */
const RECEIPT_STYLE_ID = 'receipt-page-size'

/**
 * Prints a till slip on roll paper.
 *
 * `@page { size: 80mm auto }` lives in receipt.css, and on paper it was not
 * enough: a rule sitting in a stylesheet from page load competes with whatever
 * the driver last reported, and Chrome resolves that by keeping the driver's
 * length, usually A4. The receipt is then laid out onto A4 and cut wherever
 * that runs out, which is the torn slip people see after a preview that looked
 * right.
 *
 * Written into the document immediately before printing, the same rule is
 * taken: this is the trick printMenu already relies on, and the reason is the
 * same, the unnamed @page in effect at the moment print() is called is the
 * one browsers honour.
 *
 * The length is stated rather than left as `auto`. A roll has no page length,
 * so `auto` asks the browser to invent one and it answers with the driver's.
 * A number it cannot argue with is what keeps one slip on one length of paper.
 */
export function printReceipt(heightMm?: number): void {
  document.getElementById(RECEIPT_STYLE_ID)?.remove()

  const style = document.createElement('style')
  style.id = RECEIPT_STYLE_ID
  // Rounded up to the next centimetre: a page a hair shorter than its content
  // spills one line onto a second length of paper, and on a roll the extra
  // millimetres cost nothing, the paper is cut where the content ends.
  const mm = heightMm ? Math.ceil(heightMm / 10) * 10 : null
  style.textContent = `@page { size: 80mm ${mm ? `${mm}mm` : 'auto'}; margin: 0; }`
  document.head.appendChild(style)

  const clear = () => {
    document.getElementById(RECEIPT_STYLE_ID)?.remove()
    window.removeEventListener('afterprint', clear)
  }

  window.addEventListener('afterprint', clear)

  try {
    window.print()
  } finally {
    clear()
  }
}
